import { Head, router } from "@inertiajs/react";
import { useState } from "react";
import classNames from "classnames";

import searchIcon from "/assets/search.svg";

import Modal from "/components/Modal";
import Button from "/components/Button";
import DebouncedSearch from "/components/Admin/DebouncedSearch";
import SortableColumnHead from "/components/Admin/SortableColumnHead";

import styles from "./Index.module.css";

export default function CollectionsIndex({
  collections,
  filter_params,
  sorting_params,
}) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState();
  const [search, setSearch] = useState();

  const onPublish = async (collectionId) => {
    await router.put(`/admin/collections/${collectionId}`, { state: "publish" });
  };

  const sortingStatus = (field) => {
    if (sorting_params[0] !== field)
      return null;

    return sorting_params[1];
  };

  const onSorting = (field) => (dir) => {
    router.get(
      '/admin/collections',
      { ...filter_params, sort_by: `${field} ${dir}` }
    );
  };

  const onSearch = (value) => {
    if (value == search)
      return;

    setSearch(value);
    const q = value && value.trim();

    const sortingPart = {};
    if (sorting_params)
      sortingPart.sort_by = `${sorting_params[0]} ${sorting_params[1]}`;

    router.get(
      '/admin/collections',
      { ...filter_params, ...sortingPart, q },
      {
        preserveState: true,
        preserveScroll: true,
      }
    )
  };

  const copyLink = async (event) => {
    event.preventDefault();

    await navigator.clipboard.writeText(event.target.getAttribute("href"));
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setCollectionToDelete(null);
  };

  const handleDeleteClick = (collection) => {
    setCollectionToDelete(collection);
    setIsDeleteModalOpen(true);
  };

  const deleteCollection = async () => {
    const collectionId = collectionToDelete.id;
    await router.delete(`/admin/collections/${collectionId}`);
    setIsDeleteModalOpen(false);
  };

  const draftCollections = collections.filter((collection) => collection.draft);
  const undraftedCollections = collections.filter((collection) => !collection.draft);

  return (
    <>
      <Head>
        <title>Коллекции</title>
      </Head>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        className="modal--autowidth"
      >
        <div className="modal__text">
          Вы уверены, что хотите<br />удалить эту коллекцию?
        </div>

        <div className="modal__actions">
          <Button onClick={deleteCollection} className="btn btn--dark">Удалить</Button>
          <Button onClick={handleCloseDeleteModal}>Отменить</Button>
        </div>
      </Modal>

      <div className="table-header">
        <div className="table-header__left">
          <h2>Коллекции</h2>
        </div>

        <div className="table-header__right">
          <div className="search">
            <DebouncedSearch
              icon={<img src={searchIcon} />}
              placeholder="найти"
              onChange={onSearch}
              defaultValue={filter_params.q}
            />
          </div>
          <a href="/admin/collections/new" className="btn">Создать коллекцию</a>
        </div>
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <SortableColumnHead status={sortingStatus('title_ru')} onClick={onSorting('title_ru')}>Название коллекции</SortableColumnHead>
            <SortableColumnHead status={sortingStatus('description')} onClick={onSorting('description')}>Описание</SortableColumnHead>
            <SortableColumnHead status={sortingStatus('contact_id')} onClick={onSorting('contact_id')}>Продюсер</SortableColumnHead>
            <th>Типы макетов</th>
            <SortableColumnHead status={sortingStatus('dirty')} onClick={onSorting('dirty')}>Статус</SortableColumnHead>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {draftCollections.map((collection, index) => (
            <tr key={collection.id} className={classNames({ "tbl-last-draft": (index >= draftCollections.length - 1) })}>
              <td>{collection.title_ru}</td>
              <td>{collection.description}</td>
              <td>{collection.contact?.name_ru}</td>
              <td>
                <div className={styles.layoutContentTypes}>
                  {collection.layout_content_types.map((contentType, index) => (
                    <div key={index} className={styles.layoutContentType}>{contentType}</div>
                  ))}
                </div>
              </td>
              <td>Черновик</td>
              <td className="tbl-extra">
                <div className="tbl-actions-wrapper">
                  <div className="tbl-actions tbl-actions--hiding">
                    <div className="flex-row gap-3">
                      <a href={`/admin/collections/${collection.id}/edit`} className="lnk lnk--blue">Редактировать</a>
                      <Button onClick={() => onPublish(collection.id)} className="lnk lnk--blue">Опубликовать</Button>
                    </div>

                    <div className="flex-row">
                      <Button className="lnk lnk--red" onClick={() => handleDeleteClick(collection)}>Удалить</Button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          ))}

          {undraftedCollections.map((collection) => (
            <tr key={collection.id}>
              <td>{collection.title_ru}</td>
              <td>{collection.description}</td>
              <td>{collection.contact?.name_ru}</td>
              <td>
                <div className={styles.layoutContentTypes}>
                  {collection.layout_content_types.map((contentType, index) => (
                    <div key={index} className={styles.layoutContentType}>{contentType}</div>
                  ))}
                </div>
              </td>
              <td>{collection.dirty ? "Изменена" : "Опубликована"}</td>
              <td className="tbl-extra">
                <div className="tbl-actions-wrapper">
                  <div className="tbl-actions tbl-actions--hiding">
                    <div className="flex-row gap-3">
                      <a href={`/admin/collections/${collection.id}/edit`} className="lnk lnk--blue">Редактировать</a>
                      {collection.dirty && (
                        <Button onClick={() => onPublish(collection.id)} className="lnk lnk--blue">Опубликовать</Button>
                      )}
                    </div>

                    <div className="flex-row">
                      <Button className="lnk lnk--red" onClick={() => handleDeleteClick(collection)}>Удалить</Button>
                    </div>
                  </div>

                  <div className="tbl-actions tbl-actions--hiding tbl-actions--end">
                    <div className="flex-row gap-3">
                      <a href={collection.links.ru} onClick={copyLink} className="lnk lnk--blue nowrap">Скопировать ссылку</a>
                      <a href={collection.links.en} onClick={copyLink} className="lnk lnk--blue nowrap">Copy link</a>
                    </div>
                  </div>

                  {collection.dirty && (
                    <div className="tbl-note">
                      Последние изменения не опубликованы
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
